import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import { campaignStatuses } from '../../mocks/campaigns';
import { listCampaigns, deleteCampaign } from '../../services/campaignApi';
import toast from 'react-hot-toast';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

// Status badge component
const StatusBadge = ({ status, t }) => {
  const config = campaignStatuses[status] || campaignStatuses.draft;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
        config.color,
        config.pulse && 'animate-pulse'
      )}
    >
      {status === 'running' && <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />}
      {t(config.labelKey)}
    </span>
  );
};

// Progress bar component
const ProgressBar = ({ current, total }) => {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs text-muted whitespace-nowrap">
        {current.toLocaleString()}/{total.toLocaleString()}
      </span>
    </div>
  );
};

// Skeleton loader for table rows
const TableSkeleton = ({ crossTenant }) => (
  <>
    {[...Array(5)].map((_, i) => (
      <tr key={i} className="animate-pulse">
        <td className="px-6 py-4">
          <div className="h-4 bg-secondary rounded w-48" />
          <div className="h-3 bg-secondary rounded w-32 mt-1" />
        </td>
        {crossTenant && <td className="px-6 py-4"><div className="h-4 bg-secondary rounded w-28" /></td>}
        <td className="px-6 py-4"><div className="h-4 bg-secondary rounded w-24" /></td>
        <td className="px-6 py-4"><div className="h-6 bg-secondary rounded-full w-20" /></td>
        <td className="px-6 py-4"><div className="h-4 bg-secondary rounded w-28" /></td>
        <td className="px-6 py-4"><div className="h-4 bg-secondary rounded w-20" /></td>
        <td className="px-6 py-4"><div className="h-8 bg-secondary rounded w-16" /></td>
      </tr>
    ))}
  </>
);

// Empty state component
const EmptyState = ({ t, canCreate }) => (
  <div className="text-center py-16">
    <div className="mx-auto w-16 h-16 bg-secondary rounded-full flex items-center justify-center mb-4">
      <DocumentIcon className="w-8 h-8" style={{ color: 'var(--muted)' }} />
    </div>
    <h3 className="text-lg font-medium text-foreground mb-1">{t('campaigns.noCampaigns')}</h3>
    <p className="text-sm text-muted mb-6 max-w-sm mx-auto">
      {t('campaigns.getStarted')}
    </p>
    {canCreate && (
      <Link
        to="/campaigns/new"
        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-full hover:bg-primary-hover transition-colors"
      >
        <PlusIcon className="w-4 h-4" />
        {t('campaigns.createFirst')}
      </Link>
    )}
  </div>
);

function CampaignsList() {
  const { t, i18n } = useTranslation();
  const { can, user } = useAuth();
  // super_admin, admin, and manager can create campaigns (see permissions.js: campaigns.create)
  const canCreateCampaign = can('campaigns.create');
  // Only super_admin sees the TENANT column. Managers are tenant-scoped, so the
  // tenant column is noise for them (every row is the same tenant).
  const isCrossTenantViewer = ['super_admin'].includes(
    (user?.role || '').toLowerCase().trim()
  );
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('both');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const params = {};
        if (statusFilter !== 'all') params.status = statusFilter;
        if (scopeFilter !== 'both') params.scope = scopeFilter;
        const res = await listCampaigns(params);
        setCampaigns(res.data || []);
      } catch (err) {
        console.error('Failed to load campaigns:', err);
        toast.error(t('errors.failedLoadCampaign'));
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, [statusFilter, scopeFilter]);

  // Delete handler
  const handleDelete = async (campaignId, campaignName) => {
    if (!window.confirm(t('campaigns.deleteConfirm', { name: campaignName }))) return;
    try {
      await deleteCampaign(campaignId);
      setCampaigns(prev => prev.filter(c => c.campaign_id !== campaignId));
      toast.success(t('success.campaignDeleted'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.failedDeleteCampaign'));
    }
  };

  // Filter campaigns client-side (search only, status filtered by API)
  const filteredCampaigns = campaigns.filter(c => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (c.name || '').toLowerCase().includes(q) ||
             (c.description || '').toLowerCase().includes(q) ||
             (c.agent_name || '').toLowerCase().includes(q) ||
             (c.tenant_name || '').toLowerCase().includes(q) ||
             (c.created_by_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Format date in the active UI language (was a fixed 'en-US', so ES users saw
  // "Oct 9, 03:18 PM" — Oct-10 Amplify audit, shots/24-campaigns-es.jpg).
  const dateLocale = (i18n?.language || 'en').toLowerCase().startsWith('es') ? 'es-MX' : 'en-US';
  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString(dateLocale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('campaigns.title')}</h1>
          <p className="text-sm text-muted mt-1">
            {t('campaigns.subtitle')}
          </p>
        </div>
        {canCreateCampaign && (
          <Link
            to="/campaigns/new"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-full hover:bg-primary-hover transition-colors font-medium"
          >
            <PlusIcon className="w-5 h-5" />
            {t('campaigns.newCampaign')}
          </Link>
        )}
      </div>

      {/* Scope tabs */}
      <div className="flex gap-1 border-b border-border">
        {[['both', t('campaigns.scopeBoth', 'Todas')], ['individual', t('campaigns.scopeMine', 'M\u00edas')], ['organization', t('campaigns.scopeOrg', 'Organizaci\u00f3n')]].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setScopeFilter(val)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              scopeFilter === val ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-foreground'
            )}
          >{label}</button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            type="text"
            placeholder={t('campaigns.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-card"
          />
        </div>

        {/* Status filter */}
        <div className="relative">
          <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-9 pr-8 py-2 border border-border rounded-lg text-sm appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary bg-card"
          >
            <option value="all">{t('campaigns.allStatuses')}</option>
            <option value="running">{t('campaignStatuses.running')}</option>
            <option value="paused">{t('campaignStatuses.paused')}</option>
            <option value="completed">{t('campaignStatuses.completed')}</option>
            <option value="scheduled">{t('campaignStatuses.scheduled')}</option>
            <option value="draft">{t('campaignStatuses.draft')}</option>
            <option value="failed">{t('campaignStatuses.failed')}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background">
                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                  {t('nav.campaigns')}
                </th>
                {isCrossTenantViewer && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                    {t('campaigns.tenantLabel', 'Tenant')}
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                  {t('campaigns.agentLabel', 'Agent')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                  {t('campaigns.visibility', 'Visibilidad')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                  {t('common.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                  {t('campaigns.progress')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                  {t('campaigns.createdBy', 'Created By')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">
                  {t('campaigns.startedAt', 'Started')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <TableSkeleton crossTenant={isCrossTenantViewer} />
              ) : filteredCampaigns.length === 0 ? (
                <tr>
                  <td colSpan={isCrossTenantViewer ? 7 : 6}>
                    {campaigns.length === 0 ? (
                      <EmptyState t={t} canCreate={canCreateCampaign} />
                    ) : (
                      <div className="text-center py-12 text-muted">
                        {t('common.noResults')}
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                filteredCampaigns.map((campaign) => (
                  <tr
                    key={campaign.campaign_id}
                    className="hover:bg-hover transition-colors"
                  >
                    <td className="px-6 py-4">
                      <Link
                        to={`/campaigns/${campaign.campaign_id}`}
                        className="font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {campaign.name}
                      </Link>
                      {campaign.description && (
                        <p className="text-xs text-muted mt-0.5 truncate max-w-xs">
                          {campaign.description}
                        </p>
                      )}
                    </td>
                    {isCrossTenantViewer && (
                      <td className="px-6 py-4">
                        <span className="text-sm text-foreground">
                          {campaign.tenant_name || campaign.tenant_id || '-'}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <span className="text-sm text-foreground">{campaign.agent_name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                        (campaign.visibility || 'organization') === 'individual'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400'
                          : 'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400'
                      )}>
                        {(campaign.visibility || 'organization') === 'individual'
                          ? t('campaigns.visIndividual', 'M\u00eda')
                          : t('campaigns.visOrg', 'Org')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {/* Prefer the backend read-time display_status ('piloto' when a
                          campaign 'completed' with 0 real dials because every contact
                          was pilot-skipped) so the list never shows a false
                          "Completada" (prod-ventas-closeout-sep8). Falls back to the
                          real status. */}
                      <StatusBadge status={campaign.display_status || campaign.status} t={t} />
                    </td>
                    <td className="px-6 py-4">
                      <ProgressBar
                        current={campaign.calls_placed || 0}
                        total={campaign.total_contacts || 0}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-foreground">{campaign.created_by_name || '\u2014'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-muted">
                        {formatDate(campaign.started_at || campaign.scheduled_start)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/campaigns/${campaign.campaign_id}`}
                          className="p-2 text-muted hover:text-foreground hover:bg-hover rounded-full transition-colors"
                          title={t('campaigns.details')}
                        >
                          <EyeIcon className="w-4 h-4" />
                        </Link>
                        {['draft', 'scheduled'].includes(campaign.status) && (
                          <Link
                            to={`/campaigns/${campaign.campaign_id}/edit`}
                            className="p-2 text-muted hover:text-primary hover:bg-primary-50 rounded-full transition-colors"
                            title={t('campaigns.editCampaign', 'Edit campaign')}
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </Link>
                        )}
                        {['draft', 'scheduled'].includes(campaign.status) && (
                          <button
                            onClick={() => handleDelete(campaign.campaign_id, campaign.name)}
                            className="p-2 text-muted hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            title={t('campaigns.deleteCampaign')}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary stats */}
      {!loading && campaigns.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted">
          <span>
            {filteredCampaigns.length} / {campaigns.length} {t('nav.campaigns').toLowerCase()}
          </span>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full" />
              {campaigns.filter(c => c.status === 'running').length} {t('campaignStatuses.running').toLowerCase()}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-purple-500 rounded-full" />
              {campaigns.filter(c => c.status === 'completed').length} {t('campaignStatuses.completed').toLowerCase()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignsList;
